import { SignInIdentifier } from '@logto/schemas';
import { useEffect, useRef, useState } from 'react';

import BusyLabel from '@/shared/components/LoadingLayer/BusyLabel';

import TermsLinks from '@/components/TermsLinks';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import useContextualSignIn, { type ContextualSignInSettings } from '@/hooks/use-contextual-sign-in';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import styles from './index.module.scss';

type Props = ContextualSignInSettings & {
  readonly isEmbedded?: boolean;
  readonly isFocusRequested?: boolean;
};
type FieldProps = {
  readonly form: ReturnType<typeof useContextualSignIn>;
  readonly isEnabled: boolean;
};

const PhoneFields = ({
  form,
  isEnabled,
  isRegistrationEnabled,
}: FieldProps & { readonly isRegistrationEnabled: boolean }) => {
  const { termsOfUseUrl, privacyPolicyUrl } = useTerms();
  return (
    <>
      <label className={styles.label} htmlFor="contextual-code">
        验证码
      </label>
      <div className={styles.input}>
        <input
          id="contextual-code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="6 位短信验证码"
          value={form.code}
          disabled={!isEnabled}
          aria-busy={form.busy}
          onChange={(event) => {
            if (!form.busy) {
              form.setCode(event.target.value);
            }
          }}
        />
        <button
          type="button"
          disabled={form.busy || !isEnabled || !form.agreed || form.seconds > 0}
          onPointerDown={(event) => {
            // Keep the active input and keyboard stable while requesting a code.
            event.preventDefault();
          }}
          onClick={() => {
            void form.sendCode();
          }}
        >
          {form.seconds > 0 ? `${form.seconds} 秒后重发` : '同意并获取验证码'}
        </button>
      </div>
      <p className={styles.hint}>
        {isRegistrationEnabled
          ? '未注册的手机号将自动创建账号'
          : '当前暂未开放注册，可登录已有账号'}
      </p>
      <div className={styles.agreement}>
        <input
          id="contextual-agreement"
          type="checkbox"
          aria-label="同意使用条款和隐私政策"
          checked={form.agreed}
          disabled={form.busy || !isEnabled}
          onChange={(event) => {
            form.changeAgreement(event.target.checked);
          }}
        />
        <label htmlFor="contextual-agreement">
          点击“同意并获取验证码”即表示同意{' '}
          <TermsLinks inline termsOfUseUrl={termsOfUseUrl} privacyPolicyUrl={privacyPolicyUrl} />
        </label>
      </div>
    </>
  );
};

const PasswordFields = ({
  form,
  isEnabled,
  isEmbedded,
}: FieldProps & { readonly isEmbedded: boolean }) => {
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const [visible, setVisible] = useState(false);
  const contact =
    form.resolved.ok && form.resolved.type !== SignInIdentifier.Username
      ? form.resolved
      : undefined;
  if (form.showSingleSignOnForm) {
    return (
      <p role="status" className={styles.hint}>
        此邮箱已启用单点登录，请继续完成验证。
      </p>
    );
  }
  return (
    <>
      <label className={styles.label} htmlFor="contextual-password">
        密码
      </label>
      <div className={styles.input}>
        <input
          id="contextual-password"
          name="password"
          type={visible ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="请输入密码"
          value={form.password}
          disabled={!isEnabled}
          aria-busy={form.busy}
          onChange={(event) => {
            if (!form.busy) {
              form.setPassword(event.target.value);
            }
          }}
        />
        <button
          type="button"
          disabled={form.busy}
          aria-label={visible ? '隐藏密码' : '显示密码'}
          aria-pressed={visible}
          onClick={() => {
            setVisible(!visible);
          }}
        >
          {visible ? '隐藏' : '显示'}
        </button>
      </div>
      {isForgotPasswordEnabled && (
        <div className={styles.forgot}>
          <ForgotPasswordLink identifier={contact?.type} value={contact?.provider ?? ''} />
        </div>
      )}
      {isEmbedded && <p className={styles.hint}>还没设置密码？可以使用验证码登录。</p>}
    </>
  );
};

const ContextualHeader = ({
  isEmbedded,
  isAssociating,
  isPhoneMode,
}: {
  readonly isEmbedded: boolean;
  readonly isAssociating: boolean;
  readonly isPhoneMode: boolean;
}) =>
  !isEmbedded || isAssociating ? (
    <header className={styles.header}>
      {!isEmbedded && (
        <div className={styles.brand} role="img" aria-label="VibeHub">
          <img src="/assets/vibehub-logo-approved-04.svg" alt="" />
          <img className={styles.wordmark} src="/assets/vibehub-wordmark-figma.svg" alt="" />
        </div>
      )}
      <h1>{isAssociating ? '关联你的账号' : isPhoneMode ? '登录 / 注册' : '密码登录'}</h1>
      <p>
        {isAssociating
          ? '验证手机号或原账号后，再由你确认关联微信。原来的作品与资料继续保留。'
          : isPhoneMode
            ? '用手机号，开始发现与分享。'
            : '还没设置密码？可以使用验证码登录。'}
      </p>
    </header>
  ) : null;

/** Shared real identity form. Its host supplies only public deployment settings. */
const ContextualSignInForm = ({
  isEmbedded = false,
  isFocusRequested = false,
  ...settings
}: Props) => {
  const form = useContextualSignIn(settings);
  const field = useRef<HTMLInputElement>(null);
  const phoneMode = form.mode === 'phone';
  const associating = settings.continuation === 'wechat-account-proof';
  const enabled = phoneMode
    ? settings.options.phone_otp_enabled
    : settings.options.password_enabled;
  useEffect(() => {
    if (!isEmbedded || isFocusRequested) {
      field.current?.focus({ preventScroll: true });
    }
  }, [form.mode, isEmbedded, isFocusRequested]);
  return (
    <section className={`${styles.form}${isEmbedded ? ` ${styles.embeddedForm}` : ''}`} aria-label="登录 / 注册">
      <ContextualHeader
        isEmbedded={isEmbedded}
        isAssociating={associating}
        isPhoneMode={phoneMode}
      />
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <label className={styles.label} htmlFor="contextual-identifier">
          {phoneMode ? '手机号' : '手机号 / 账号 / 邮箱'}
        </label>
        <div className={styles.input}>
          <input
            ref={field}
            id="contextual-identifier"
            name="identifier"
            type={phoneMode ? 'tel' : 'text'}
            autoComplete={phoneMode ? 'tel' : 'username'}
            inputMode={phoneMode ? 'tel' : 'text'}
            placeholder={phoneMode ? '请输入手机号' : '输入手机号、账号或邮箱'}
            value={form.identifier}
            disabled={!enabled}
            aria-busy={form.busy}
            aria-describedby="contextual-identifier-hint"
            onChange={(event) => {
              form.changeIdentifier(event.target.value);
            }}
          />
        </div>
        <p className={styles.hint} id="contextual-identifier-hint">
          {phoneMode
            ? '国内号码直接输入；国际号码以 + 开头'
            : '账号可加 @ 前缀；手机号形状的数字账号请按 @账号 输入。'}
        </p>
        {phoneMode ? (
          <PhoneFields
            form={form}
            isEnabled={enabled}
            isRegistrationEnabled={settings.options.registration_enabled}
          />
        ) : (
          <PasswordFields form={form} isEnabled={enabled} isEmbedded={isEmbedded} />
        )}
        {!enabled && (
          <p role="status" className={styles.hint}>
            当前方式暂不可用，请切换其他登录方式。
          </p>
        )}
        <div className={styles.feedback}>
          {form.error ? (
            <p role="alert" className={styles.error}>
              {form.error}
            </p>
          ) : (
            <p role="status" className={styles.notice}>
              {form.notice}
            </p>
          )}
        </div>
        <CaptchaBox />
        <button type="submit" className={styles.primary} disabled={form.busy || !enabled} aria-busy={form.busy}>
          <BusyLabel busy={form.busy}>{associating
              ? '验证并继续'
              : phoneMode
                ? '注册并登录'
                : form.showSingleSignOnForm
                  ? '单点登录'
                  : '登录'}</BusyLabel>
        </button>
      </form>
      <button
        type="button"
        className={styles.switch}
        disabled={form.busy}
        onClick={form.changeMode}
      >
        {phoneMode ? '使用密码登录' : '使用验证码注册并登录'}
      </button>
    </section>
  );
};

export default ContextualSignInForm;
